import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import type {
  RestorationJob,
  RestorationJobResult,
} from "../editor/types";
import type {
  RestorationProgress as WorkerRestorationProgress,
  RestorationWorkerMessage,
} from "../workers/restorationWorker";

const NO_PROGRESS_TIMEOUT_MS = 90_000;

type RestorationProvider = "webgpu" | "wasm";

export type RestorationProgress = WorkerRestorationProgress;

export interface RestorationResult {
  imageData: ImageData;
  provider: RestorationProvider;
}

interface PendingRequest {
  resolve: (result: RestorationResult) => void;
  reject: (error: Error) => void;
  onProgress?: (progress: RestorationProgress) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

export interface RestorationProcessor {
  (
    imageData: ImageData,
    strength: number,
    onProgress?: (progress: RestorationProgress) => void,
  ): Promise<RestorationResult>;
  cancel: () => void;
}

function createRestorationWorker(): Worker {
  return new Worker(
    new URL("../workers/restorationWorker.ts", import.meta.url),
    { type: "module" },
  );
}

export function useRestorationProcessor(): RestorationProcessor {
  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);
  const pendingRef = useRef(new Map<number, PendingRequest>());
  const mountedRef = useRef(false);
  const restartWorkerRef = useRef<(error: Error) => void>(() => undefined);

  useEffect(() => {
    mountedRef.current = true;

    const clearPending = (error: Error) => {
      pendingRef.current.forEach((pending) => {
        clearTimeout(pending.timeoutId);
        pending.reject(error);
      });
      pendingRef.current.clear();
    };

    const detachAndTerminate = (worker: Worker | null) => {
      if (!worker) return;
      worker.onmessage = null;
      worker.onerror = null;
      worker.onmessageerror = null;
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
    };

    const armTimeout = (
      requestId: number,
      pending: PendingRequest,
    ) => {
      clearTimeout(pending.timeoutId);
      pending.timeoutId = setTimeout(() => {
        if (pendingRef.current.get(requestId) !== pending) return;
        restartWorkerRef.current(
          new Error(
            "AI復元の進捗が90秒間停止しました。もう一度お試しください",
          ),
        );
      }, NO_PROGRESS_TIMEOUT_MS);
    };

    const attachWorker = () => {
      if (!mountedRef.current) return;
      const worker = createRestorationWorker();
      workerRef.current = worker;

      worker.onmessage = (
        event: MessageEvent<RestorationWorkerMessage>,
      ) => {
        const message = event.data;
        const pending = pendingRef.current.get(message.requestId);
        if (!pending) return;

        if ("progress" in message) {
          armTimeout(message.requestId, pending);
          pending.onProgress?.(message.progress);
          return;
        }

        clearTimeout(pending.timeoutId);
        pendingRef.current.delete(message.requestId);
        const result = message as RestorationJobResult;
        if (result.error || !result.imageData) {
          pending.reject(
            new Error(result.error ?? "AI復元結果を取得できませんでした"),
          );
          return;
        }
        pending.resolve({
          imageData: result.imageData,
          provider: result.provider ?? "wasm",
        });
      };

      worker.onerror = (event) => {
        event.preventDefault();
        if (workerRef.current !== worker) return;
        restartWorkerRef.current(
          new Error(
            "AI復元ワーカーが停止しました。もう一度お試しください",
          ),
        );
      };

      worker.onmessageerror = () => {
        if (workerRef.current !== worker) return;
        restartWorkerRef.current(
          new Error(
            "AI復元結果を読み取れませんでした。もう一度お試しください",
          ),
        );
      };
    };

    restartWorkerRef.current = (error: Error) => {
      detachAndTerminate(workerRef.current);
      clearPending(error);
      attachWorker();
    };

    attachWorker();

    return () => {
      mountedRef.current = false;
      restartWorkerRef.current = () => undefined;
      detachAndTerminate(workerRef.current);
      clearPending(new Error("AI復元がキャンセルされました"));
    };
  }, []);

  const process = useCallback(
    (
      imageData: ImageData,
      strength: number,
      onProgress?: (progress: RestorationProgress) => void,
    ): Promise<RestorationResult> => {
      const worker = workerRef.current;
      if (!worker) {
        return Promise.reject(
          new Error("AI復元ワーカーを初期化できませんでした"),
        );
      }

      const requestId = ++requestIdRef.current;
      const copiedData = new Uint8ClampedArray(imageData.data);
      const transferableImage = new ImageData(
        copiedData,
        imageData.width,
        imageData.height,
      );
      const job: RestorationJob = {
        requestId,
        imageData: transferableImage,
        strength,
      };

      return new Promise((resolve, reject) => {
        const pending: PendingRequest = {
          resolve,
          reject,
          onProgress,
          timeoutId: setTimeout(() => {
            if (pendingRef.current.get(requestId) !== pending) return;
            restartWorkerRef.current(
              new Error(
                "AI復元の進捗が90秒間停止しました。もう一度お試しください",
              ),
            );
          }, NO_PROGRESS_TIMEOUT_MS),
        };
        pendingRef.current.set(requestId, pending);
        try {
          worker.postMessage(job, [copiedData.buffer]);
        } catch (error) {
          clearTimeout(pending.timeoutId);
          pendingRef.current.delete(requestId);
          reject(
            error instanceof Error
              ? error
              : new Error("AI復元を開始できませんでした"),
          );
        }
      });
    },
    [],
  );

  const cancel = useCallback(() => {
    if (pendingRef.current.size === 0) return;
    restartWorkerRef.current(new Error("AI復元がキャンセルされました"));
  }, []);

  return useMemo(
    () => Object.assign(process, { cancel }),
    [cancel, process],
  );
}
